// src/components/RPA/FindingsComparisonView.js
// ⭐ NEW: Human-in-the-Loop Feature 3 — WP-810 Dual-Track Findings Comparison.
//
// Fetches GET /admin/rpa/docs/:docId/findings-comparison and renders the
// four-category overlay:
//   agree (green) · ai_only_exception (amber) · auditor_only_exception (red)
//   · amount_variance (blue)
// Renders only after independent findings are submitted (the endpoint
// enforces this with a 403 otherwise).

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const CATEGORY_META = {
  agree: {
    label: 'Agreement',
    icon: '✅',
    color: '#166534', bg: '#f0fdf4', border: '#bbf7d0'
  },
  ai_only_exception: {
    label: 'AI Exception — Auditor No Exception',
    icon: '⚠️',
    color: '#92400e', bg: '#fff7ed', border: '#fdba74'
  },
  auditor_only_exception: {
    label: 'Auditor Exception — AI Missed (CRITICAL)',
    icon: '🚩',
    color: '#991b1b', bg: '#fef2f2', border: '#fca5a5'
  },
  amount_variance: {
    label: 'Both Exception — Amount Variance',
    icon: '↔️',
    color: '#1e3a8a', bg: '#eff6ff', border: '#93c5fd'
  }
};

const fmtAmt = (n) =>
  Number.isFinite(Number(n)) ? `$${Math.abs(Number(n)).toLocaleString()}` : '—';

const FindingsComparisonView = ({ docId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);

  const load = useCallback(async (rebuild = false) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${API_URL}/admin/rpa/docs/${docId}/findings-comparison${rebuild ? '?rebuild=true' : ''}`,
        { withCredentials: true }
      );
      if (data.success) setComparison(data.comparison);
      else setError(data.message || 'Failed to load comparison.');
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { if (docId) load(false); }, [docId, load]);

  if (!docId) return null;

  if (loading) {
    return (
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, marginTop: 16, fontSize: 12, color: '#6b7280' }}>
        ⏳ Building findings comparison…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: 16, marginTop: 16, fontSize: 12, color: '#991b1b' }}>
        {error}
      </div>
    );
  }

  if (!comparison) return null;

  const s = comparison.completion_summary || {};
  const items = comparison.items || [];
  const order = ['auditor_only_exception', 'amount_variance', 'ai_only_exception', 'agree'];

  return (
    <div style={{ border: '1px solid #0ea5e9', borderRadius: 10, background: '#f8fafc', padding: 16, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0c4a6e' }}>
            📊 {comparison.workpaper_ref} — Dual-Track Findings Comparison
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
            AI findings {comparison.comparison_basis?.ai_findings_generated_at ? new Date(comparison.comparison_basis.ai_findings_generated_at).toLocaleString() : '—'}
            {' · '}Auditor findings {comparison.comparison_basis?.human_findings_submitted_at ? new Date(comparison.comparison_basis.human_findings_submitted_at).toLocaleString() : '—'}
            {comparison.comparison_basis?.auditor?.name ? ` (${comparison.comparison_basis.auditor.name})` : ''}
          </div>
        </div>
        <button
          onClick={() => load(true)}
          style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #0ea5e9', color: '#0369a1', background: '#fff', borderRadius: 6, cursor: 'pointer' }}
        >
          ↻ Rebuild
        </button>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: s.agreement_rate_pct >= 80 ? '#16a34a' : s.agreement_rate_pct >= 50 ? '#d97706' : '#dc2626' }}>
            {s.agreement_rate_pct}%
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Agreement</div>
        </div>
        {order.map(cat => {
          const meta = CATEGORY_META[cat];
          const count = s[cat] ?? items.filter(i => i.category === cat).length;
          return (
            <div key={cat} style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: meta.color }}>{count}</div>
              <div style={{ fontSize: 10, color: meta.color, maxWidth: 130 }}>{meta.icon} {meta.label}</div>
            </div>
          );
        })}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', textAlign: 'center' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: s.variances_requiring_resolution > 0 ? '#dc2626' : '#16a34a' }}>
            {s.variances_resolved ?? 0}/{s.variances_requiring_resolution ?? 0}
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>Variances resolved</div>
        </div>
      </div>

      {/* Materiality reference */}
      {comparison.materiality_reference && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
          Materiality: overall {fmtAmt(comparison.materiality_reference.overallMateriality)}
          {' · '}performance {fmtAmt(comparison.materiality_reference.performanceMateriality)}
          {' · '}trivial threshold {fmtAmt(comparison.materiality_reference.trivialThreshold)}
        </div>
      )}

      {/* Category sections, variances first */}
      {order.map(cat => {
        const meta = CATEGORY_META[cat];
        const catItems = items.filter(i => i.category === cat);
        if (!catItems.length) return null;
        return (
          <details key={cat} open={cat !== 'agree'} style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: meta.color }}>
              {meta.icon} {meta.label} ({catItems.length})
            </summary>
            <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
              {catItems.map((item, i) => (
                <div key={i} style={{ background: meta.bg, border: `1px solid ${meta.border}`, borderRadius: 8, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                    <strong style={{ fontSize: 13, color: meta.color }}>{item.account}</strong>
                    {cat === 'amount_variance' && (
                      <span style={{ fontSize: 12, color: meta.color, fontWeight: 600 }}>
                        AI {fmtAmt(item.aiAmount)} vs Auditor {fmtAmt(item.auditorAmount)} · Δ {fmtAmt(item.amountVariance)}
                        {item.exceedsPerformanceMateriality ? ' — exceeds performance materiality' : ''}
                      </span>
                    )}
                    {cat === 'ai_only_exception' && item.addressedByAuditor === false && (
                      <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 6px' }}>
                        Not assessed by auditor
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8, fontSize: 12 }}>
                    <div style={{ background: '#fff', borderRadius: 6, padding: 8, border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>🤖 AI TRACK</div>
                      {item.ai?.length ? item.ai.map((ex, j) => (
                        <div key={j} style={{ marginBottom: 4 }}>
                          <span style={{ color: '#dc2626', fontWeight: 600 }}>{ex.severity}</span>{' '}
                          {ex.issue} {ex.amount !== null && <strong>({fmtAmt(ex.amount)})</strong>}
                          {ex.source && <span style={{ color: '#9ca3af' }}> · {ex.source}</span>}
                        </div>
                      )) : <span style={{ color: '#16a34a' }}>No exception raised</span>}
                    </div>
                    <div style={{ background: '#fff', borderRadius: 6, padding: 8, border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', marginBottom: 4 }}>✍️ AUDITOR TRACK</div>
                      {item.auditor ? (
                        item.auditor.assessment === 'exception' ? (
                          <div>
                            <span style={{ color: '#dc2626', fontWeight: 600 }}>{item.auditor.severity}</span>{' '}
                            {item.auditor.issue} {item.auditor.amount !== null && item.auditor.amount !== undefined && <strong>({fmtAmt(item.auditor.amount)})</strong>}
                          </div>
                        ) : (
                          <span style={{ color: '#16a34a' }}>No exception</span>
                        )
                      ) : (
                        <span style={{ color: '#9ca3af' }}>Account not assessed</span>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>{item.note}</div>

                  {cat !== 'agree' && (
                    <div style={{ fontSize: 11, marginTop: 6, color: item.resolution ? '#166534' : '#92400e' }}>
                      {item.resolution
                        ? `✅ Resolved: ${item.resolution.rationale || ''}`
                        : '⏳ Resolution pending — required before opinion sign-off (Feature 4).'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
};

export default FindingsComparisonView;